// HighlightReel stat picker — 4 fixed marquee slots, deterministic and
// curated. NO ranking ladder; each slot answers a specific question.
//
// Field paths verified against actual funStats output:
//   stats.championships.titleCounts is { owner: [year, year] }
//   stats.singleGame.highest[0] has .pts (NOT .points)
//   stats.streaks.longestWinStreaks (NOT stats.streaks.win)
//   stats.singleSeason.highestPPG (NOT stats.singleSeasonRecords)
//   stats.singleGame.biggestBlowouts[0] has .margin
//
// Returned array order matches slot order in AlmanacHero — first item is
// leftmost cell on desktop. Empty slots (no data) get value: "—" so the
// grid stays balanced rather than collapsing irregularly.

export function pickHighlightReel(stats) {
  if (!stats) return [];
  return [
    slotMostTitlesOrMostWins(stats),
    slotSingleGameRecord(stats),
    slotLongestWinStreak(stats),
    slotBestSeasonOrBlowout(stats),
  ];
}

// Slot 1 — Most championships when there's a clear leader; falls back to
// Most Wins when no owner has ≥2 titles. Rationale (A1.6 R2 #9): a single-
// title leader is just "whichever owner happens to be sorted first" since
// every winner has the same count. Most Wins is a real superlative for
// young/balanced leagues and stays meaningful for mature ones too.
function slotMostTitlesOrMostWins(stats) {
  const counts = stats.championships?.titleCounts || {};
  let best = null;
  for (const [owner, years] of Object.entries(counts)) {
    const count = Array.isArray(years) ? years.length : 0;
    if (count <= 0) continue;
    if (!best || count > best.count) best = { owner, count };
  }
  // Unique-leader guard — don't name a single "Most Titles" winner when
  // multiple owners are tied at the top (the rendered name would just be
  // whichever happened to sort first). 2+ titles AND a strict majority is
  // required; otherwise fall through to Most Wins.
  if (best && best.count >= 2) {
    const tiedAtTop = Object.values(counts)
      .filter(y => (y?.length || 0) === best.count).length;
    if (tiedAtTop === 1) {
      return {
        value: String(best.count),
        valueSuffix: "titles",
        label: `Most Titles — ${best.owner}`,
      };
    }
  }
  // No clear titles leader — fall back to career wins. careerTotals is
  // already sorted by totalW desc upstream, so row 0 is the leader.
  const winsLeader = stats.careerTotals?.[0];
  if (winsLeader) {
    const w = winsLeader.totalW ?? winsLeader.w;
    return {
      value: String(w),
      valueSuffix: "wins",
      label: `Most Wins — ${winsLeader.owner}`,
    };
  }
  return { value: "—", label: "Most Wins" };
}

// Slot 2 — Highest single-game score ever.
function slotSingleGameRecord(stats) {
  const top = stats.singleGame?.highest?.[0];
  if (!top) return { value: "—", label: "Most Points in a Single Game" };
  return {
    value: Number(top.pts).toFixed(1),
    valueSuffix: "pts",
    label: `Most Points in a Single Game — ${top.owner}`,
  };
}

// Slot 3 — Longest win streak. Unique-leader guard: if two+ owners share
// the longest streak length, don't single one out; render the slot
// without an owner name so we're not highlighting an arbitrary first-sort
// winner as "the" streak holder.
function slotLongestWinStreak(stats) {
  const streaks = stats.streaks?.longestWinStreaks || [];
  const top = streaks.find(s => (s?.length || 0) > 0);
  if (!top) return { value: "—", label: "Longest Win Streak" };
  const tiedAtTop = streaks.filter(s => (s?.length || 0) === top.length).length;
  if (tiedAtTop > 1) {
    return {
      value: String(top.length),
      valueSuffix: top.length === 1 ? "win" : "in a row",
      label: `Longest Win Streak — ${tiedAtTop}-way tie`,
    };
  }
  return {
    value: String(top.length),
    valueSuffix: top.length === 1 ? "win" : "in a row",
    label: `Longest Win Streak — ${top.owner}`,
  };
}

// Slot 4 — Highest season PPG when ≥150; otherwise biggest blowout margin.
// The PPG threshold filters out leagues where nobody had a truly dominant
// season; in those cases a single-game blowout margin is more superlative.
function slotBestSeasonOrBlowout(stats) {
  const ppgTop = stats.singleSeason?.highestPPG?.[0];
  const ppgVal = ppgTop ? Number(ppgTop.ppg) : 0;
  if (ppgVal >= 150) {
    return {
      value: ppgVal.toFixed(1),
      valueSuffix: "PPG",
      label: `Highest Scoring Team — ${ppgTop.owner}`,
    };
  }
  const blowout = stats.singleGame?.biggestBlowouts?.[0];
  if (blowout) {
    return {
      value: `+${Number(blowout.margin).toFixed(1)}`,
      valueSuffix: "margin",
      label: `Biggest Blowout — ${blowout.owner}`,
    };
  }
  // Last-resort fallback — return the PPG entry even if it's below 150,
  // since the slot needs SOMETHING in it.
  if (ppgTop) {
    return {
      value: ppgVal.toFixed(1),
      valueSuffix: "PPG",
      label: `Highest Scoring Team — ${ppgTop.owner}`,
    };
  }
  return { value: "—", label: "Highest Scoring Team" };
}
